import axios from 'axios';

/**
 * Choosing and starting a release, shared by the two places that do it: the
 * title page, where nobody should have to read a list of torrents to watch a
 * film, and the player, where changing quality means starting a different
 * release at the same point in the film.
 */

// Mirrors the server's ranking. Only used to keep an automatic pick from
// dropping a resolution tier, never to re-order what the server already ranked.
const RESOLUTION_RANK = { '2160p': 5, '1440p': 4, '1080p': 3, '720p': 2, '576p': 1, '480p': 1 };

const resRank = (release) => RESOLUTION_RANK[release?.resolution] ?? 0;

/**
 * How high the automatic pick may reach.
 *
 * 4K is the tier where the largest files meet the thinnest swarms — measured
 * on this server, 2160p auto-picks took 13–34s to first frame while 1080p
 * started in 3–8s — so Auto stops at 1440p unless the household raises the
 * cap on the Data page. This caps only what plays *unasked*: every tier the
 * indexers returned still appears in the player's quality menu, and a title
 * that exists only above the cap still plays rather than erroring.
 */
const DEFAULT_AUTO_MAX = '1440p';
let autoMaxResolution = DEFAULT_AUTO_MAX;

export const AUTO_QUALITY_CHOICES = ['1080p', '1440p', '2160p'];

export const setAutoQualityCap = (value) => {
  autoMaxResolution = AUTO_QUALITY_CHOICES.includes(value) ? value : DEFAULT_AUTO_MAX;
};

export const autoQualityCap = () => autoMaxResolution;

const overCap = (release) => resRank(release) > RESOLUTION_RANK[autoMaxResolution];

/* ----- audio language --------------------------------------------------- */

/**
 * The audio languages that can be asked for. Mirrors SEARCH_WORDS in the
 * server's services/indexer/languages.js - a language only belongs here if the
 * server knows how release names write it, or it could be chosen and never
 * found.
 *
 * `original` is the work's own language, whatever that is for the title on
 * screen, and is the default: the household watched original audio only until
 * 2026-10-16, and anyone who never opens the setting still does.
 */
export const AUDIO_LANGUAGES = [
  { code: 'original', label: 'Original language' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ru', label: 'Russian' },
  { code: 'pl', label: 'Polish' },
  { code: 'nl', label: 'Dutch' },
  { code: 'tr', label: 'Turkish' },
  { code: 'ar', label: 'Arabic' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'kn', label: 'Kannada' },
  { code: 'bn', label: 'Bengali' },
  { code: 'ur', label: 'Urdu' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'sv', label: 'Swedish' },
  { code: 'no', label: 'Norwegian' },
  { code: 'da', label: 'Danish' },
  { code: 'fi', label: 'Finnish' },
  { code: 'th', label: 'Thai' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'id', label: 'Indonesian' },
  { code: 'cs', label: 'Czech' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'ro', label: 'Romanian' },
  { code: 'el', label: 'Greek' },
  { code: 'he', label: 'Hebrew' },
  { code: 'uk', label: 'Ukrainian' }
];

const AUDIO_CODES = new Set(AUDIO_LANGUAGES.map((entry) => entry.code));

export const audioLanguageLabel = (code) =>
  AUDIO_LANGUAGES.find((entry) => entry.code === code)?.label || code;

// The profile's default, handed over by the Library provider the way the
// quality cap is - this lib has no context of its own.
let preferredAudioLanguage = 'original';

export const setPreferredAudio = (value) => {
  preferredAudioLanguage = AUDIO_CODES.has(value) ? value : 'original';
};

export const preferredAudio = () => preferredAudioLanguage;

/**
 * The spellings a container uses for each language. ffprobe reports what the
 * muxer wrote, which is ISO 639-2 - both its bibliographic and terminological
 * forms turn up ("fre" and "fra") - and occasionally the two-letter code.
 */
const TRACK_CODES = {
  en: ['eng'],
  fr: ['fre', 'fra'],
  es: ['spa'],
  de: ['ger', 'deu'],
  it: ['ita'],
  pt: ['por'],
  ru: ['rus'],
  pl: ['pol'],
  nl: ['dut', 'nld'],
  tr: ['tur'],
  ar: ['ara'],
  hi: ['hin'],
  ta: ['tam'],
  te: ['tel'],
  ja: ['jpn'],
  ko: ['kor'],
  zh: ['chi', 'zho', 'cmn', 'yue'],
  ml: ['mal'],
  kn: ['kan'],
  bn: ['ben'],
  ur: ['urd'],
  pa: ['pan'],
  sv: ['swe'],
  no: ['nor', 'nob', 'nno'],
  da: ['dan'],
  fi: ['fin'],
  th: ['tha'],
  vi: ['vie'],
  id: ['ind'],
  cs: ['cze', 'ces'],
  hu: ['hun'],
  ro: ['rum', 'ron'],
  el: ['gre', 'ell'],
  he: ['heb'],
  uk: ['ukr']
};

/** Whether a probed audio track is in the ISO 639-1 language `code`. */
export const trackIsLanguage = (track, code) => {
  const lang = String(track?.language || '').toLowerCase();
  const want = String(code || '').toLowerCase();
  if (!lang || !want || lang === 'und') return false;
  return lang === want || (TRACK_CODES[want] || []).includes(lang);
};

/** The ISO 639-1 code a probed track is in, when it is one we know. */
export const trackLanguageCode = (track) => {
  const lang = String(track?.language || '').toLowerCase();
  if (!lang || lang === 'und') return null;
  if (TRACK_CODES[lang]) return lang;
  return Object.keys(TRACK_CODES).find((code) => TRACK_CODES[code].includes(lang)) || null;
};

/**
 * Whether a swarm can deliver a release faster than it plays.
 *
 * This is the difference between a film starting and a spinner. Ranking by
 * resolution alone picks 2160p every time, and 2160p is exactly where the
 * largest files meet the smallest swarms: measured on a 4K remux with 3 seeders,
 * 2.6 Mbit/s was arriving against the ~35 Mbit/s the file plays at, so it could
 * never have produced a frame no matter how long anyone waited. The release with
 * the best picture is reliably the one that cannot be watched.
 *
 * So a release is judged first on whether it can keep ahead of itself, and only
 * then on how it looks.
 */

// What one seeder is worth, measured off this server's own swarms. Deliberately
// conservative: over-estimating throughput is what puts someone in front of a
// stall, while under-estimating only costs a resolution tier.
const PER_SEEDER_BPS = 900_000;

// webtorrent will not hold more connections than this per torrent, so seeders
// past it do not make the download any faster and must not score as if they did.
const PEER_CEILING = 40;

// Bytes have to arrive faster than they are consumed, not merely as fast, or the
// buffer never builds and every hiccup in the swarm becomes a visible stall.
const HEADROOM = 1.6;

// Used only when the caller doesn't know the runtime — the continue-watching
// rail, for one, remembers what to search for but not how long it runs. Wrong by
// a few minutes only shifts the estimate slightly; wrong by an order of
// magnitude is what would matter, and neither default is.
const ASSUMED_MINUTES = { movie: 110, series: 45 };

/** Bits per second the file must be fed at to play in real time. */
const requiredBitrate = (release, minutes) => {
  const seconds = Math.max(minutes, 1) * 60;
  return (Number(release?.sizeBytes) || 0) * 8 / seconds;
};

/** Bits per second the swarm is expected to actually deliver. */
const expectedThroughput = (release) =>
  PER_SEEDER_BPS * Math.min(Number(release?.seeders) || 0, PEER_CEILING);

/**
 * How comfortably a release outruns its own bitrate. Above 1 it plays; the
 * further above, the more it can absorb a swarm going quiet mid-scene.
 */
export const headroomFor = (release, { type, runtimeMinutes } = {}) => {
  // A cached debrid link is served from a datacentre over plain HTTP. There is
  // no swarm to model and it is never the bottleneck.
  if (release?.cached === true) return Infinity;

  const minutes = Number(runtimeMinutes) || ASSUMED_MINUTES[type] || ASSUMED_MINUTES.movie;
  const required = requiredBitrate(release, minutes);
  // Nothing to divide by — size is unknown, so this can't be judged either way
  if (!required) return 1;

  return expectedThroughput(release) / (required * HEADROOM);
};

const isStreamable = (release, context) => headroomFor(release, context) >= 1;

// How many releases an automatic play will try before giving up. The first pick
// is usually right; when it isn't, the failure is a dead magnet or a debrid
// provider refusing it, and the next release down is a better answer than an
// error message. Beyond three it stops being a hiccup and starts being a wait.
const ATTEMPTS = 3;

// How many releases a play opens between them; see raceTorrents on the server.
const RACE_FIELD = 10;

/**
 * Whether a file's length rules it out. Generous on purpose: a series' runtime is
 * only its typical episode. Mirrors runtimeRulesOut in services/verdicts.js.
 */
export const runtimeRulesOut = (durationSeconds, runtimeMinutes) => {
  const seconds = Number(durationSeconds);
  const minutes = Number(runtimeMinutes);
  if (!(seconds > 0) || !(minutes > 0)) return false;
  const ratio = seconds / 60 / minutes;
  return ratio < 0.55 || ratio > 2.2;
};

/** What the player has learned about a release. Losing one of these costs nothing. */
export const reportVerdict = (release, { season, episode, verdict, reason }) => {
  if (!release?.workKey) return Promise.resolve();
  return axios
    .post('/api/torrents/verdict', {
      infoHash: release.infoHash,
      work: release.workKey,
      season,
      episode,
      verdict,
      reason
    })
    .catch(() => {});
};

export const searchReleases = async ({
  name,
  originalName,
  originalLanguage,
  type,
  imdbId,
  year,
  season,
  episode,
  audioLanguage,
  // The server's cap. Its list leads with the best picture, so a shorter one
  // could cut the healthy 720p that was the only thing anyone was seeding.
  limit = 100
}) => {
  const params = new URLSearchParams({
    // `any` reaches the film and TV indexers alike; see routes/search.js.
    type: ['series', 'any'].includes(type) ? type : 'movie',
    limit: String(limit)
  });
  if (imdbId) params.set('imdbId', imdbId);
  if (year) params.set('year', String(year));
  if (season != null) params.set('season', String(season));
  if (episode != null) params.set('episode', String(episode));
  // The work's own title and language, when the catalog knows them. The title
  // is a second query the server runs - it is how a foreign film released only
  // under its original name gets found - and the language is what the server's
  // dub gate judges bare language tags against.
  if (originalName) params.set('originalTitle', originalName);
  if (originalLanguage) params.set('originalLanguage', originalLanguage);
  // The language to look for. Without one the profile's default is asked,
  // which is `original` unless someone changed it in Settings.
  params.set('audioLanguage', audioLanguage || preferredAudioLanguage);

  const { data } = await axios.get(`/api/search/${encodeURIComponent(name)}?${params}`);
  return { results: data.data || [], meta: data.meta || null };
};

/**
 * Order the ranked list for playing without being asked.
 *
 * The server's order is right for someone reading a list: it leads with the best
 * picture, and a person reading it can see the seeder count and judge for
 * themselves. Playing automatically has nobody to do that judging, so the first
 * question becomes whether the release can be watched at all — a 2160p remux
 * three people are seeding is not a better answer than a 1080p web-dl five
 * thousand are, it is not an answer.
 *
 * So the releases that can keep ahead of their own bitrate come first, best
 * picture among them; and only if none can does it fall back to whichever comes
 * closest, since a long buffer still beats an error message.
 */
export const autoOrder = (results, context = {}) => {
  const scored = results.map((release, index) => ({
    release,
    index,
    headroom: headroomFor(release, context),
    streamable: isStreamable(release, context)
  }));

  const anyStreamable = scored.some((entry) => entry.streamable);

  return scored
    .sort((a, b) => {
      // Before anything else, including whether it can be streamed at all.
      //
      // This is the one place the "can it be watched" rule above has to give
      // way, and it is worth being clear why. A cam is small and heavily seeded
      // - it is *made* to be downloaded - so it wins the throughput test against
      // every real release of a film still in cinemas, which are large and
      // thinly seeded. Judging on streamability first therefore does not pick
      // the best watchable release; it picks the cam, every time, and hands
      // someone a camcorder recording of a cinema screen without ever saying so.
      //
      // Sorting it last can mean the automatic pick now starts something that
      // buffers. That is the right way round: a wait is recoverable and the
      // quality menu says which tiers can sustain playback, whereas the cam was
      // being presented as the film itself.
      const byCam = Number(Boolean(a.release?.cam)) - Number(Boolean(b.release?.cam));
      if (byCam) return byCam;

      // A release in the language that was asked for, before whether it
      // streams comfortably. The server already ranks these first; without
      // this the streamability test below would hand back the better-seeded
      // original every time, since dubs are the thinner swarms. Only on a
      // language search - an original search carries no `audioMatch`.
      const byLanguage = Number(Boolean(b.release?.audioMatch)) - Number(Boolean(a.release?.audioMatch));
      if (byLanguage) return byLanguage;

      if (!anyStreamable) {
        // Nothing here can be streamed comfortably. Under-cap first — the cap
        // exists because the tiers above it are the slow ones — then closest.
        const byOverCap = Number(overCap(a.release)) - Number(overCap(b.release));
        if (byOverCap) return byOverCap;
        const byHeadroom = b.headroom - a.headroom;
        if (byHeadroom) return byHeadroom;
        return a.index - b.index;
      }

      // Can it be watched — before how it looks
      const byStreamable = Number(b.streamable) - Number(a.streamable);
      if (byStreamable) return byStreamable;

      // The household's quality cap, below streamability on purpose: a 4K that
      // plays still beats a 1080p that buffers. Among releases that all play,
      // the capped tiers sink — which is what makes Auto reach for 1080p/1440p
      // and leaves 4K to the quality menu unless the Data page allows it.
      const byOverCap = Number(overCap(a.release)) - Number(overCap(b.release));
      if (byOverCap) return byOverCap;

      const tier = resRank(b.release) - resRank(a.release);
      if (tier) return tier;

      // A cached debrid release starts in about a second and never stalls, so
      // it wins ties within its own resolution — but it never jumps a tier,
      // because that would quietly hand someone 720p when 1080p was sitting
      // right there.
      const cached = Number(b.release.cached === true) - Number(a.release.cached === true);
      if (cached) return cached;

      // Among equals, the healthier swarm starts sooner and stalls less
      const byHeadroom = b.headroom - a.headroom;
      if (byHeadroom) return byHeadroom;

      return a.index - b.index;
    })
    .map((entry) => entry.release);
};

/**
 * One entry per resolution the title is actually available in, best release
 * first. This is the quality menu: the viewer picks a picture, not a torrent.
 *
 * Each tier carries whether its best release can actually sustain playback, so
 * the menu can say so up front. Choosing 2160p and watching it never start is a
 * worse experience than being told the swarm cannot carry it.
 */
export const qualityTiers = (results, context = {}) => {
  const byResolution = new Map();

  for (const release of autoOrder(results, context)) {
    const key = release.resolution || 'SD';
    if (!byResolution.has(key)) {
      byResolution.set(key, {
        key,
        label: key.toUpperCase(),
        release,
        count: 1,
        streamable: isStreamable(release, context)
      });
    } else {
      byResolution.get(key).count += 1;
    }
  }

  // Ordered by picture, not by health: this is a menu someone reads and picks
  // from, and a resolution list that reorders itself is unreadable. The health
  // of each row is shown on the row instead.
  return [...byResolution.values()].sort((a, b) => resRank(b.release) - resRank(a.release));
};

/**
 * Turn a release into something the player can open.
 *
 * A cached debrid link plays over plain HTTP with no swarm to join, so it's
 * tried first; anything else falls through to WebTorrent on this server. A pack
 * with no obvious episode inside it needs a human, and says so.
 */
const openDebrid = async (release, { season, episode }) => {
  try {
    const { data } = await axios.post('/api/debrid/resolve', {
      magnet: release.magnet,
      season,
      episode
    });
    const playUrl = data.data?.playUrl;
    if (playUrl) return { src: playUrl };
  } catch (err) {
    // Kept for the source log: "why didn't this play instantly" is worth
    // answering even when P2P then carries it.
    release.debridError = err?.response?.data?.error || err?.message || 'debrid refused';
  }
  return null;
};

export const startRelease = async (release, { debrid, season, episode } = {}) => {
  // `cached === true` is only ever set by a provider that can answer the
  // question. Real-Debrid and AllDebrid cannot — their releases arrive as
  // `null`, meaning unknown — so gating on `=== true` collapsed unknown into
  // "not cached" and skipped debrid on *every* play, sending a paid-for account's
  // whole library to the swarm. That is the exact failure the three-state rule
  // exists to prevent, and it is why nothing played instantly.
  //
  // Unknown is worth asking. The server polls for a bounded window and returns a
  // null url if the release genuinely is not cached, which falls through to
  // WebTorrent below. Only an explicit `false` — a provider that checked and
  // said no — skips debrid outright.
  if (debrid && release.cached !== false) {
    // A debrid refusal is not a verdict on the release. `infringing_file` means
    // the provider won't serve this hash, and a benched account means it can't
    // serve anything right now — the swarm is unaffected by either, so both fall
    // through to WebTorrent rather than failing the release outright. Letting
    // this throw would skip P2P for a title it can play perfectly well.
    const instant = await openDebrid(release, { season, episode });
    if (instant) return instant;
  }

  const { data } = await axios.post('/api/torrents/add', {
    magnet: release.magnet,
    season,
    episode
  });
  const { torrentId, selectedFileId } = data.data;

  if (selectedFileId == null) return { torrentId, needsPick: true };
  return { torrentId, fileId: selectedFileId };
};

// The human-readable reason a release didn't open, for the source log. Server
// errors (a debrid refusal, a dead add) carry their own message; anything else
// falls back to the raw error.
const attemptReason = (err) => err?.response?.data?.error || err?.message || 'could not open';

/**
 * Open the ordered candidates and return the first to answer with the right file
 * in it. Throws when none do.
 *
 * A release the server found no video in does not count as opened. Some are
 * honest season packs whose numbering the parser could not read, but plenty are
 * fakes — a 1.2GB `.exe` named like a 1080p rip is a real thing the indexers
 * return, and it ranks well because it is small and well seeded. Pressing Play
 * should not land anyone on a picker offering to run one, so these are passed
 * over while there is anything else left to try.
 *
 * `context.onAttempt` is called once per release tried, in order, with the
 * outcome — this is what the source log reads to explain why an episode that
 * found releases still wouldn't play.
 */
/**
 * Whether there is anything on the other end to download from.
 *
 * A release nobody is seeding cannot produce a frame however long it is given,
 * so opening it only spends the server's peer-finding timeout and then fails.
 * Three of those in a row is several minutes of nothing, which is exactly what
 * "it just doesn't play" looked like from the sofa. A cached debrid link has no
 * swarm behind it at all and is always live.
 */
const canDeliver = (release) => release?.cached === true || (Number(release?.seeders) || 0) > 0;

// How many dead releases to name in the source log before it stops being
// informative and starts being a wall of text.
const SKIPS_LOGGED = 3;

export const startBest = async (candidates, context = {}) => {
  const { onAttempt } = context;
  let failure = null;

  // Known-good first, then what something is seeding. The rest stay in the field
  // behind them rather than being dropped: the counts come from the indexers and
  // are often hours old, so a release showing no seeders can still be the one that
  // answers. Nobody can tell from here; opening them is how you find out.
  const pool = [
    ...candidates.filter((release) => release.verified),
    ...candidates.filter((release) => !release.verified && canDeliver(release)),
    ...candidates.filter((release) => !release.verified && !canDeliver(release))
  ];

  // Every entry carries a `seq`. A caller rendering these as they arrive uses it
  // to turn the "trying" row into that release's outcome, rather than appending
  // a second row for the same release.
  let seq = 0;

  // A debrid link needs no swarm and starts at once, so it is worth asking first.
  // `debrid` is only set while an account can actually answer.
  if (context.debrid) {
    for (const release of pool.filter((entry) => entry.cached !== false).slice(0, ATTEMPTS)) {
      const mine = seq++;
      if (onAttempt) onAttempt({ release, outcome: 'trying', seq: mine });
      const target = await openDebrid(release, context);
      if (target) {
        if (onAttempt) onAttempt({ release, outcome: 'playing', seq: mine });
        return { release, target };
      }
      if (onAttempt) onAttempt({ release, outcome: 'failed', reason: release.debridError || 'not cached', seq: mine });
    }
  }

  const field = pool.slice(0, RACE_FIELD);
  const rows = new Map();
  for (const release of field.slice(0, ATTEMPTS)) {
    const mine = seq++;
    rows.set(release.infoHash, mine);
    if (onAttempt) onAttempt({ release, outcome: 'trying', seq: mine });
  }

  const settle = (results) => {
    if (!onAttempt) return;
    for (const { infoHash, outcome } of results || []) {
      if (outcome === 'not-tried') continue;
      const release = field.find((entry) => entry.infoHash === infoHash);
      if (!release) continue;
      const mine = rows.has(infoHash) ? rows.get(infoHash) : seq++;
      if (outcome === 'playing') onAttempt({ release, outcome: 'playing', seq: mine });
      else if (outcome === 'nofile') onAttempt({ release, outcome: 'needs-pick', seq: mine });
      else if (outcome === 'overtaken' || outcome === 'answered') onAttempt({ release, outcome: 'overtaken', seq: mine });
      else if (outcome === 'slow') onAttempt({ release, outcome: 'failed', reason: 'too slow to play', seq: mine });
      else onAttempt({ release, outcome: 'failed', reason: 'no peers answered', seq: mine });
    }
  };

  try {
    const { data } = await axios.post('/api/torrents/race', {
      releases: field.map(({ magnet, infoHash }) => ({ magnet, infoHash })),
      work: field[0]?.workKey,
      season: context.season,
      episode: context.episode
    });
    const raced = data.data;
    settle(raced.field);
    const release = field.find((entry) => entry.infoHash === raced.infoHash);

    // Nothing played on its own. A pack someone can choose out of still beats an
    // error, so it is the fallback rather than the first answer.
    if (raced.selectedFileId == null) return { release, target: { torrentId: raced.torrentId, needsPick: true } };
    return { release, target: { torrentId: raced.torrentId, fileId: raced.selectedFileId } };
  } catch (err) {
    settle(err?.response?.data?.field);
    failure = err;
  }

  throw failure || new Error('No release could be opened');
};

/**
 * What the player needs to know about what it is playing. It is reachable by URL
 * and does no catalog lookup of its own, so anything missing here it cannot find
 * out — captions, the quality menu and watch history all read from this.
 */
export const labelFor = (title, { season, episode, release, episodeName, audioLanguage } = {}) => ({
  torrentId: release?.infoHash,
  // The catalog id, which is what "what episode comes after this one" is
  // answered from. Missing on streams opened by bare URL, and everything that
  // needs it (the next-episode offer, the way back to the episode list) simply
  // doesn't appear — same as captions without an imdbId.
  id: title.id ?? null,
  title: title.name,
  // What the catalog says this runs, which the player holds the file up against
  runtimeMinutes: title.runtimeMinutes || null,
  // Carried so the player's own searches - the quality menu, the next-episode
  // prefetch, the continue-watching re-find - ask the same widened,
  // dub-filtered question the title page did.
  originalName: title.originalName || null,
  originalLanguage: title.originalLanguage || null,
  // The language this stream was searched in, so the player's own searches -
  // quality, next episode, resume - stay in it.
  audioLanguage: audioLanguage || preferredAudioLanguage,
  subtitle: [
    season != null && episode != null ? `S${season} E${episode}` : null,
    episodeName || null,
    release?.resolution || null
  ]
    .filter(Boolean)
    .join(' · '),
  backdrop: title.backdrop,
  imdbId: title.imdbId || null,
  // The catalog's own id, so the recommendation seeds built from watch history
  // don't need an IMDb-id lookup to get back to the catalog's vocabulary
  catalogId: title.id || null,
  type: title.type,
  year: title.year || title.firstAirYear || null,
  season: season ?? null,
  episode: episode ?? null,
  // Which release is playing, so the quality menu can show it as the active one
  infoHash: release?.infoHash || null,
  resolution: release?.resolution || null
});

/** Where a started release should be opened. */
export const routeFor = (target) => {
  if (target.src) return `/watch?src=${encodeURIComponent(target.src)}`;
  if (target.needsPick) return `/torrent/${target.torrentId}`;
  return `/player/${target.torrentId}/${target.fileId}`;
};
